import React, { useEffect, useState } from 'react';
import { useEditor } from '../../context/EditorContext';
import { LargeButton } from './RibbonPrimitives';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuItem } from '../ui/dropdown-menu';
import { ALargeSmall, Check, Trash2, BookmarkPlus, Loader2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { BUILTIN_FONT_SCHEMES, getCustomFontSchemes, loadFontSchemes, onFontSchemesChanged, deleteFontScheme, applyFontScheme, documentFontRoles } from '../../lib/fontSchemes';
import { ensureFonts } from '../../lib/fontLoader';
import { apiErrorMessage } from '../../lib/projectApi';

/** Re-render when the user's font schemes change. */
const useCustomFontSchemes = () => {
  const [, bump] = useState(0);
  useEffect(() => { loadFontSchemes(); return onFontSchemesChanged(() => bump((n) => n + 1)); }, []);
  return getCustomFontSchemes();
};

/** Apply a font scheme to the whole publication (one undoable step) — shared by the ribbon menu and the Save dialog. */
export const useApplyFontScheme = () => {
  const { state, dispatch } = useEditor();
  return (scheme) => {
    const { pages, masters, count } = applyFontScheme(state.pages, state.masters, scheme, state.fontScheme);
    if (count) dispatch({ type: 'REPLACE_DOCUMENT_CONTENT', payload: { pages, masters } });
    dispatch({ type: 'SET_FONT_SCHEME', payload: { id: scheme.id, name: scheme.name, heading: scheme.heading, body: scheme.body } });
    ensureFonts([scheme.heading, scheme.body]);
    toast.success(`Font scheme: ${scheme.name}`, {
      description: count ? `${scheme.heading} for headings, ${scheme.body} for body text · ${count} place${count === 1 ? '' : 's'} updated. Ctrl+Z undoes it.` : `${scheme.heading} for headings, ${scheme.body} for body text — the publication already used these fonts.`,
    });
    return count;
  };
};

/**
 * A button inside a menu row that must not select (= apply) the row: pointerdown AND pointerup are stopped because Radix
 * synthesises a click (→ onSelect) on pointerup when it missed the pointerdown.
 */
const RowButton = ({ onClick, title, className = '', testId, children }) => (
  <button
    type="button"
    onPointerDown={(e) => e.stopPropagation()}
    onPointerUp={(e) => e.stopPropagation()}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onClick(); } }}
    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClick(); }}
    className={`shrink-0 rounded-sm p-1 text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ${className}`}
    title={title}
    aria-label={title}
    data-testid={testId}
  >
    {children}
  </button>
);

const SchemeRow = ({ scheme, active, onApply, onEdit, onDelete }) => (
  <DropdownMenuItem onSelect={() => onApply(scheme)} className="flex items-center gap-2 py-1.5 pr-1 text-[13px] cursor-pointer" data-testid={`font-scheme-item-${scheme.id}`} data-state={active ? 'active' : undefined}>
    <span className="w-4 shrink-0 text-primary">{active && <Check className="h-3.5 w-3.5" />}</span>
    <span className="flex-1 min-w-0">
      <span className="block truncate text-foreground">{scheme.name}</span>
      <span className="block truncate text-[11px] text-muted-foreground"><span style={{ fontFamily: `"${scheme.heading}"` }}>{scheme.heading}</span> · <span style={{ fontFamily: `"${scheme.body}"` }}>{scheme.body}</span></span>
    </span>
    <span className="shrink-0 leading-none text-foreground" aria-hidden="true"><span className="text-[17px] font-semibold" style={{ fontFamily: `"${scheme.heading}"` }}>A</span><span className="text-[13px]" style={{ fontFamily: `"${scheme.body}"` }}>a</span></span>
    {onEdit && <RowButton onClick={() => onEdit(scheme)} title={`Edit “${scheme.name}” (rename or change its fonts)`} className="ml-1 hover:text-foreground hover:bg-muted" testId={`font-scheme-edit-${scheme.id}`}><Pencil className="h-3.5 w-3.5" /></RowButton>}
    {onDelete && <RowButton onClick={() => onDelete(scheme)} title={`Delete “${scheme.name}”`} className="hover:text-destructive hover:bg-destructive/10" testId={`font-scheme-delete-${scheme.id}`}><Trash2 className="h-3.5 w-3.5" /></RowButton>}
  </DropdownMenuItem>
);

/** Page Design ▸ Schemes ▸ Fonts: built-in and saved heading + body pairings; applying one re-fonts the publication. */
export const FontSchemesMenu = () => {
  const { state, openDialog } = useEditor();
  const custom = useCustomFontSchemes();
  const apply = useApplyFontScheme();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const activeId = state.fontScheme?.id || null;
  const activeName = state.fontScheme?.name;

  // preview faces for the menu
  useEffect(() => { if (open) ensureFonts([...BUILTIN_FONT_SCHEMES, ...custom].flatMap((s) => [s.heading, s.body])); }, [open, custom]);

  const remove = async (scheme) => {
    setBusy(scheme.id);
    try { await deleteFontScheme(scheme.id); toast.success(`Font scheme “${scheme.name}” deleted`); } catch (e) { toast.error(await apiErrorMessage(e, 'The font scheme could not be deleted.')); } finally { setBusy(null); }
  };

  const saveCurrent = () => {
    const roles = documentFontRoles(state.pages, state.masters, state.fontScheme);
    openDialog('font-scheme', { heading: roles.heading, body: roles.body });
  };
  /** Edit a saved scheme in place (same id): rename or change its faces, optionally re-applying it. */
  const edit = (scheme) => { setOpen(false); openDialog('font-scheme', { editId: scheme.id, name: scheme.name, heading: scheme.heading, body: scheme.body }); };

  return (
    <DropdownMenu modal={false} open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <LargeButton icon={ALargeSmall} label={'Fonts'} tip={activeName ? `Font scheme: ${activeName} — pick another heading + body pairing for the whole publication` : 'Font schemes: a heading + body font pairing for the whole publication'} testId="ribbon-command-font-schemes" chevron />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="bg-popover text-popover-foreground border border-border shadow-md w-[280px] z-[80] max-h-[70vh] overflow-y-auto" data-ribbon onCloseAutoFocus={(e) => e.preventDefault()} data-testid="font-schemes-menu">
        <DropdownMenuLabel className="text-[11px] font-medium text-muted-foreground">Built-in</DropdownMenuLabel>
        {BUILTIN_FONT_SCHEMES.map((s) => <SchemeRow key={s.id} scheme={s} active={activeId === s.id} onApply={apply} />)}
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[11px] font-medium text-muted-foreground flex items-center gap-2">My font schemes {busy && <Loader2 className="h-3 w-3 animate-spin" />}</DropdownMenuLabel>
        {custom.length === 0 && <div className="px-2 pb-1.5 text-[11px] text-muted-foreground" data-testid="font-schemes-empty">None yet — save the pairing this publication uses, or a “Pairs well with” suggestion from the font list.</div>}
        {custom.map((s) => <SchemeRow key={s.id} scheme={s} active={activeId === s.id} onApply={apply} onEdit={edit} onDelete={remove} />)}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={saveCurrent} className="text-[13px] cursor-pointer" data-testid="font-scheme-save-current"><BookmarkPlus className="h-4 w-4 mr-2 text-muted-foreground" /> Save Current Fonts as Scheme…</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
